import { ApiService } from "./ApiService.js";
import { LocalStorage } from "./LocalStorage.js";
import { LoginService } from "./LoginService.js";
import { ServiceConsts } from "./ServiceConsts.js";
import { snippetsUtils } from "../utils/snippetsUtils.js";
export class TopicService {
    static async getBaseUrl() {
        let tenant = await LocalStorage.get("activeTenant");

        return tenant == null || tenant.personal
            ? `${ServiceConsts.API_URL}/topics`
            : `${ServiceConsts.API_URL}/tenants/${tenant.id}/topics`;
    }

    static async getTopic(topicId) {
        const baseUrl = await this.getBaseUrl();

        let topic = await ApiService.get(`${baseUrl}/${topicId}`);
        topic.snippets = await snippetsUtils.sortByHighestWeight(
            topic.snippets,
        );
        return topic;
    }

    static async getInboxSnippets() {
        const baseUrl = await this.getBaseUrl();

        let snippets = await ApiService.get(`${baseUrl}/snippets`);
        snippets = await snippetsUtils.sortByHighestWeight(snippets);
        return snippets;
    }

    static createTopic = async (topic) => {
        const baseUrl = await this.getBaseUrl();

        return await ApiService.post(baseUrl, topic);
    };

    static updateTopic = async (topic) => {
        const baseUrl = await this.getBaseUrl();

        topic = {
            ...topic,
            roles: undefined,
            snippets: undefined,
            tenant: undefined,
        };
        return await ApiService.put(`${baseUrl}/${topic.id}`, topic);
    };

    static deleteTopic = async (topicId) => {
        const baseUrl = await this.getBaseUrl();

        return await ApiService.delete(`${baseUrl}/${topicId}`);
    };

    static updatePublicRole = async (publicRole, topicId) => {
        const baseUrl = await this.getBaseUrl();

        let data = {
            publicRole: publicRole,
        };

        return await ApiService.patch(`${baseUrl}/${topicId}`, data);
    };

    static async createTopicRole(topicId, topicRole) {
        const baseUrl = await this.getBaseUrl();

        return await ApiService.post(`${baseUrl}/${topicId}/roles`, topicRole);
    }

    static async updateTopicRole(topicId, topicRole) {
        const baseUrl = await this.getBaseUrl();

        return await ApiService.patch(
            `${baseUrl}/${topicId}/roles/${topicRole.id}`,
            topicRole,
        );
    }

    static async deleteTopicRole(topicId, topicRoleId) {
        const baseUrl = await this.getBaseUrl();

        return await ApiService.delete(
            `${baseUrl}/${topicId}/roles/${topicRoleId}`,
        );
    }

    static getSnippets = async (topicId) => {
        const baseUrl = await this.getBaseUrl();

        let snippets = await ApiService.get(`${baseUrl}/${topicId}/snippets`);

        snippets = snippetsUtils.sortByHighestWeight(snippets);

        return snippets;
    };

    static getSnippet = async (snippetId, topicId) => {
        const baseUrl = await this.getBaseUrl();

        if (topicId) {
            return await ApiService.get(
                `${baseUrl}/${topicId}/snippets/${snippetId}`,
            );
        } else {
            return await ApiService.get(`${baseUrl}/snippets/${snippetId}`);
        }
    };

    static getArchivedSnippets = async (topicId, limit, offset) => {
        const baseUrl = await this.getBaseUrl();

        if (topicId) {
            return await ApiService.get(
                `${baseUrl}/${topicId}/archive?limit=${limit}&offset=${offset}`,
            );
        } else {
            return await ApiService.get(`${baseUrl}/archive?limit=${limit}&offset=${offset}`);
        }
    };

    static searchArchivedSnippets = async (topicId, searchTerm, limit, offset) => {
        const baseUrl = await this.getBaseUrl();

        if (topicId) {
            return await ApiService.get(
                `${baseUrl}/${topicId}/archive/search?q=${encodeURIComponent(searchTerm)}&limit=${limit}&offset=${offset}`,
            );
        } else {
            return await ApiService.get(`${baseUrl}/archive/search?q=${encodeURIComponent(searchTerm)}&limit=${limit}&offset=${offset}`);
        }
    };

    static createSnippet = async (snippet, topicId) => {
        const baseUrl = await this.getBaseUrl();

        snippet = {
            ...snippet,

            // these are genreated by the server
            createdOn: undefined,
            createdBy: undefined,
        };

        let snippetResponse;
        if (topicId) {
            snippetResponse = await ApiService.post(
                `${baseUrl}/${topicId}/snippets`,
                snippet,
            );
        } else {
            snippetResponse = await ApiService.post(
                `${baseUrl}/snippets`,
                snippet,
            );
        }

        let auth = await LoginService.getAuth();

        // update snippet's fields
        snippetResponse = {
            ...snippetResponse,
            createdOn: new Date().getTime(),
            createdBy: auth.user,
        };

        return snippetResponse;
    };

    static updateSnippet = async (snippet, topicId) => {
        const baseUrl = await this.getBaseUrl();

        let savedSnippet = {
            id: snippet.id,
            name: snippet.name ?? undefined,
            weight: snippet.weight ?? undefined,
            topicId: snippet.topicId ?? topicId,
            verified: snippet.verified ?? undefined,
            url: snippet.url ?? undefined,
        };

        if (topicId) {
            return ApiService.patch(
                `${baseUrl}/${topicId}/snippets/${savedSnippet.id}`,
                savedSnippet,
            );
        }

        return ApiService.patch(`${baseUrl}/snippets/${snippet.id}`, snippet);
    };

    static moveSnippetToInbox = async (snippetId, topicId) => {
        const baseUrl = await this.getBaseUrl();

        return ApiService.patch(
            `${baseUrl}/${topicId}/snippets/${snippetId}/remove`,
            {
                snippetId: snippetId,
                topicId: topicId,
            },
        );
    };

    static deleteSnippet = async (snippetId, topicId) => {
        const baseUrl = await this.getBaseUrl();

        if (topicId) {
            return ApiService.delete(
                `${baseUrl}/${topicId}/snippets/${snippetId}`,
            );
        }

        return ApiService.delete(`${baseUrl}/snippets/${snippetId}`);
    };

    static createSnippetFromUrl = async (data, topicId) => {
        const baseUrl = await this.getBaseUrl();

        let snippetResponse;
        if (topicId) {
            snippetResponse = await ApiService.post(
                `${baseUrl}/${topicId}/urlSnippets`,
                data,
            );
        } else {
            snippetResponse = await ApiService.post(
                `${baseUrl}/urlSnippets`,
                data,
            );
        }

        let auth = await LoginService.getAuth();

        // update snippet's fields
        snippetResponse = {
            ...snippetResponse,
            createdOn: new Date().getTime(),
            createdBy: auth.user,
        };

        return snippetResponse;
    };

    static updateReaction = async (snippetId, topicId, type) => {
        const baseUrl = await this.getBaseUrl();

        return ApiService.post(
            `${baseUrl}/${topicId}/snippets/${snippetId}/reactions`,
            {
                type: type,
            },
        );
    };

    static deleteReaction = async (snippetId, topicId, reactionId) => {
        const baseUrl = await this.getBaseUrl();

        return ApiService.delete(
            `${baseUrl}/${topicId}/snippets/${snippetId}/reactions/${reactionId}`,
        );
    };

    static createComment = async (comment, snippetId, topicId) => {
        comment = {
            ...comment,

            // these are genreated by the server
            createdOn: undefined,
            createdBy: undefined,
        };

        let auth = await LoginService.getAuth();

        const baseUrl = await this.getBaseUrl();

        let commentResponse;

        if (topicId) {
            commentResponse = await ApiService.post(
                `${baseUrl}/${topicId}/snippets/${snippetId}/comments`,
                comment,
            );
        } else {
            commentResponse = await ApiService.post(
                `${baseUrl}/snippets/${snippetId}/comments`,
                comment,
            );
        }

        // update comment's fields
        commentResponse = {
            ...commentResponse,
            createdOn: new Date().getTime(),
            createdBy: auth.user,
        };

        return commentResponse;
    };

    static updateComment = async (comment, snippetId, topicId) => {
        comment = {
            ...comment,

            // these are genreated by the server
            createdOn: undefined,
            createdBy: undefined,
        };

        const baseUrl = await this.getBaseUrl();

        if (topicId) {
            return ApiService.put(
                `${baseUrl}/${topicId}/snippets/${snippetId}/comments/${comment.id}`,
                comment,
            );
        } else {
            return ApiService.put(
                `${baseUrl}/snippets/${snippetId}/comments/${comment.id}`,
                comment,
            );
        }
    };

    static async deleteComment(commentId, snippetId, topicId) {
        const baseUrl = await this.getBaseUrl();

        if (topicId) {
            return ApiService.delete(
                `${baseUrl}/${topicId}/snippets/${snippetId}/comments/${commentId}`,
            );
        } else {
            return ApiService.delete(
                `${baseUrl}/snippets/${snippetId}/comments/${commentId}`,
            );
        }
    }

    static async removeTagFromSnippet(tagId, snippetId, topicId) {
        const baseUrl = await this.getBaseUrl();

        if (topicId) {
            return ApiService.delete(
                `${baseUrl}/${topicId}/snippets/${snippetId}/tags/${tagId}`,
            );
        } else {
            return ApiService.delete(
                `${baseUrl}/snippets/${snippetId}/tags/${tagId}`,
            );
        }
    }

    static async addTagToSnippet(tag, snippetId, topicId) {
        const baseUrl = await this.getBaseUrl();

        const url = topicId
            ? `${baseUrl}/${topicId}/snippets/${snippetId}/tags`
            : `${baseUrl}/snippets/${snippetId}/tags`;

        return await ApiService.post(url, tag);
    }

    static async addTagToSnippetWithRetry(
        tag,
        snippetId,
        topicId,
        retries = 5,
        delay = 1000,
    ) {
        let attempt = 0;

        while (attempt < retries) {
            try {
                return await this.addTagToSnippet(tag, snippetId, topicId);
            } catch (error) {
                attempt++;
                if (attempt >= retries) {
                    console.error(`Failed after ${attempt} attempts`, error);
                    throw error;
                }
                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }
    }

    /**
     * Get all collaborators for a topic. Will return an array of users (backend's CollaboratorResponse object).
     * @param {*} tenantId - tenant id for the topic
     * @param {*} topicId - topic id for the topic
     * @returns array of users
     */
    static async getCollaborators(topicId) {
        const baseUrl = await this.getBaseUrl();
        return ApiService.get(`${baseUrl}/${topicId}/collaborators`);
    }

    static async getSharedTopic(topicId) {
        // get topic without user token
        const sharedTopic = await fetch(
            `${ServiceConsts.SERVER_URL}/api/topics/${topicId}`,
            {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            },
        );

        if (sharedTopic.ok) {
            let topic = await sharedTopic.json();

            topic.snippets = await snippetsUtils.sortByHighestWeight(
                topic.snippets,
            );

            return topic;
        } else throw { isError: true, error: sharedTopic };
    }

    static async getRecentTopics() {
        const baseUrl = await this.getBaseUrl();
        let topics = await ApiService.get(`${baseUrl}/recent`);
        return topics;
    }

    static async copyTopic(tenantId, topicId, clusterId, copyComments) {
        const baseUrl = await this.getBaseUrl();
        const url = `${baseUrl}/${topicId}/copy`;
        const body = {
            tenantId: tenantId,
            copyComments: copyComments,
        };
        if (clusterId) {
            body.clusterId = clusterId;
        }
        return ApiService.post(url, body);
    }

    static async getSnippetSuggestions(content) {
        const baseUrl = await this.getBaseUrl();

        let snippet = {
            content: content,
        };

        const url = `${baseUrl}/snippets/suggestions`;

        return ApiService.post(url, snippet);
    }


    static async getKnowledgeTriggers(pageUrl) {
        try {
            const baseUrl = await this.getBaseUrl();
            const url = `${baseUrl}/knowledge-triggers?pageUrl=${encodeURIComponent(pageUrl)}`;
            return await ApiService.get(url);
            
        } catch (error) {
            console.error("Failed to fetch knowledge triggers:", error);
            return [];
        }
    }


}

    

